import CloseIcon from "@mui/icons-material/Close";
import { Box, Dialog, IconButton, Stack, Typography } from "@mui/material";
import moment from "moment";
import "moment/locale/tr";
import { useEffect, useMemo, useState } from "react";
import { CreditStatus, DebtType } from "../../Models/Credit";
import {
  BORDER,
  BORDER_LIGHT,
  BORDER_STRONG,
  CARD_SHADOW,
  FOCUS_RING,
  INK,
  SOFT_BG,
  SUCCESS,
  TEXT_FAINT,
  TEXT_MUTED,
} from "../../Utils/designTokens";
import { getRemoteText } from "../../Utils/firebase";
import CLoadingButton from "../CLoadingButton/CLoadingButton";
import { FormatAmountSymbol } from "../FormatData/FormatAmount";

/**
 * CRL-3241: "Toplu Öde" modal (new-design.html `#bulkpay-modal`) shown before
 * navigating to checkout. The backend only supports paying everything or a
 * user-entered amount (type CUSTOM), so the design's per-payment selection tab
 * is replaced by a read-only "Tümü" list of the loaded pending payments.
 */

type BulkPayMode = "all" | "amount";

/** Nearest-due allocation of a custom amount, as previewed in the modal. */
export interface BulkPayAllocation {
  coveredCreditIds: number[];
  partial?: { creditId: number; amount: number };
}

export interface BulkPayModalProps {
  open: boolean;
  onClose: () => void;
  /** Loaded pending payments from the store (informative lists only). */
  payments: DebtType[];
  /** Authoritative total debt (`CreditSummary.totalAmount`); custom amounts are capped to it. */
  totalAmount: number;
  /**
   * `customAmount` (and the previewed `allocation`) are set only when confirmed
   * from the "Tutar gir" tab (→ PaymentType.CUSTOM); both are undefined for
   * pay-everything (→ PaymentType.ALL).
   */
  onConfirm: (customAmount?: number, allocation?: BulkPayAllocation) => void;
}

const rowLogoSx = {
  width: "34px",
  height: "34px",
  borderRadius: "9px",
  objectFit: "cover",
  flexShrink: 0,
  boxShadow: "inset 0 0 0 1px rgba(0,0,0,.08)",
} as const;

const BulkPayModal = ({
  open,
  onClose,
  payments,
  totalAmount,
  onConfirm,
}: BulkPayModalProps) => {
  const [mode, setMode] = useState<BulkPayMode>("all");
  const [amountText, setAmountText] = useState("");

  useEffect(() => {
    if (open) {
      setMode("all");
      setAmountText("");
    }
  }, [open]);

  /** Design `pendSorted()`: pending payments by nearest due date first. */
  const pendingSorted = useMemo(() => {
    const dueTime = (p: DebtType) => {
      const t = new Date(p.dueDate).getTime();
      return Number.isNaN(t) ? Number.MAX_SAFE_INTEGER : t;
    };
    return payments
      .filter((p) => p.status !== CreditStatus.PAID)
      .slice()
      .sort((a, b) => dueTime(a) - dueTime(b));
  }, [payments]);

  const roundedTotal = Math.round(totalAmount * 100) / 100;
  const enteredAmount = amountText
    ? Math.round(parseFloat(amountText.replace(",", ".")) * 100) / 100 || 0
    : 0;
  const cappedAmount = Math.min(enteredAmount, roundedTotal);

  /** Design `allocate()`: close payments from the nearest due date, last one may be partial. */
  const allocation = useMemo(() => {
    const covered: DebtType[] = [];
    let partial: { payment: DebtType; amount: number } | null = null;
    let remainingCents = Math.round(cappedAmount * 100);
    for (const p of pendingSorted) {
      const dueCents = Math.round((p.remainingPaymentAmount || 0) * 100);
      if (dueCents <= 0) continue;
      if (remainingCents >= dueCents) {
        covered.push(p);
        remainingCents -= dueCents;
      } else {
        if (remainingCents > 0) {
          partial = { payment: p, amount: remainingCents / 100 };
        }
        break;
      }
    }
    return { covered, partial };
  }, [pendingSorted, cappedAmount]);

  const payableAmount = mode === "all" ? roundedTotal : cappedAmount;
  const payDisabled = payableAmount <= 0;

  /** Number → input text with a kuruş comma: 3545.93 → "3545,93", 700 → "700". */
  const toAmountText = (value: number) => {
    const cents = Math.round(value * 100);
    const lira = Math.floor(cents / 100);
    const kurus = cents % 100;
    return kurus === 0 ? String(lira) : `${lira},${String(kurus).padStart(2, "0")}`;
  };

  const presets = [
    {
      label: getRemoteText("bulk_pay_preset_half", "Yarısı"),
      value: Math.round(roundedTotal * 50) / 100,
    },
    {
      label: getRemoteText("bulk_pay_preset_full", "Tamamı"),
      value: roundedTotal,
    },
  ];

  /**
   * Kuruş-aware amount input: both "," and "." act as the decimal separator
   * (max 2 decimals); dots alongside a comma, repeated, or followed by 3+
   * digits are treated as pasted Turkish thousands separators ("1.234,56").
   */
  const handleAmountChange = (raw: string) => {
    let s = raw.replace(/[^\d.,]/g, "");
    if (s.includes(",")) {
      s = s.replace(/\./g, "");
    } else {
      const dots = (s.match(/\./g) || []).length;
      const decimals = dots === 1 ? s.split(".")[1] : "";
      s =
        dots === 1 && decimals.length <= 2
          ? s.replace(".", ",")
          : s.replace(/\./g, "");
    }
    const [intRaw = "", ...decRest] = s.split(",");
    const intPart = intRaw.slice(0, 7);
    setAmountText(
      s.includes(",")
        ? `${intPart || "0"},${decRest.join("").slice(0, 2)}`
        : intPart
    );
  };

  const tabSx = (active: boolean) =>
    ({
      flex: 1,
      height: "42px",
      border: 0,
      borderRadius: "9px",
      background: active ? "#FFFFFF" : "none",
      color: active ? INK : TEXT_MUTED,
      fontFamily: "inherit",
      fontSize: "14px",
      fontWeight: 700,
      cursor: "pointer",
      transition: "background .15s, color .15s",
      boxShadow: active ? CARD_SHADOW : "none",
    } as const);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      scroll="body"
      PaperProps={{
        sx: {
          borderRadius: "24px",
          maxWidth: "480px",
          width: "100%",
          m: 2,
        },
      }}
    >
      <Box sx={{ position: "relative", p: "28px 26px 24px" }}>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            top: "14px",
            right: "14px",
            color: TEXT_FAINT,
            "&:hover": { background: BORDER_LIGHT, color: INK },
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography
          align="center"
          sx={{ fontSize: "21px", fontWeight: 800, color: INK, px: 4 }}
        >
          {getRemoteText("bulk_pay_title", "Toplu Öde")}
        </Typography>

        {/* Tabs (design .bulkpay__tabs) */}
        <Stack
          direction="row"
          role="tablist"
          sx={{
            gap: "6px",
            background: BORDER_LIGHT,
            borderRadius: "12px",
            p: "4px",
            m: "18px 0",
          }}
        >
          <Box
            component="button"
            type="button"
            role="tab"
            aria-selected={mode === "all"}
            onClick={() => setMode("all")}
            sx={tabSx(mode === "all")}
          >
            {getRemoteText("bulk_pay_tab_all", "Tümü")}
          </Box>
          <Box
            component="button"
            type="button"
            role="tab"
            aria-selected={mode === "amount"}
            onClick={() => setMode("amount")}
            sx={tabSx(mode === "amount")}
          >
            {getRemoteText("bulk_pay_tab_amount", "Tutar gir")}
          </Box>
        </Stack>

        {mode === "all" ? (
          /* "Tümü": all loaded pending payments, read-only (no per-payment selection) */
          <Stack
            sx={{
              maxHeight: "320px",
              overflowY: "auto",
              gap: "8px",
            }}
          >
            {pendingSorted.map((p) => (
              <Stack
                key={p.id}
                direction="row"
                alignItems="center"
                sx={{
                  gap: "12px",
                  p: "12px",
                  border: `1.5px solid ${BORDER}`,
                  borderRadius: "14px",
                }}
              >
                <Box
                  component="img"
                  src={p.shop.logoUrl}
                  alt={p.shop.name}
                  sx={rowLogoSx}
                />
                <Stack sx={{ minWidth: 0, flex: 1, lineHeight: 1.25 }}>
                  <Typography
                    noWrap
                    sx={{ fontSize: "14px", fontWeight: 700, color: INK }}
                  >
                    {p.shop.name}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: TEXT_FAINT }}>
                    {moment(p.dueDate).locale("tr").format("D MMMM YYYY")}
                  </Typography>
                </Stack>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 800,
                    color: INK,
                    whiteSpace: "nowrap",
                  }}
                >
                  {FormatAmountSymbol(p.remainingPaymentAmount)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        ) : (
          /* "Tutar gir": custom amount + nearest-due allocation preview */
          <Stack>
            <Stack
              direction="row"
              alignItems="center"
              sx={{
                gap: "6px",
                border: `1.5px solid ${BORDER_STRONG}`,
                borderRadius: "14px",
                p: "0 18px",
                height: "64px",
                mb: "12px",
                transition: "border-color .15s, box-shadow .15s",
                "&:focus-within": {
                  borderColor: INK,
                  boxShadow: `0 0 0 4px ${FOCUS_RING}`,
                },
              }}
            >
              <Typography
                component="span"
                sx={{ fontSize: "26px", fontWeight: 800, color: TEXT_FAINT }}
              >
                ₺
              </Typography>
              <Box
                component="input"
                type="text"
                inputMode="decimal"
                autoComplete="off"
                placeholder="0"
                value={amountText}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleAmountChange(e.target.value)
                }
                sx={{
                  flex: 1,
                  width: "100%",
                  minWidth: 0,
                  height: "100%",
                  border: 0,
                  outline: 0,
                  background: "none",
                  fontFamily: "inherit",
                  fontSize: "28px",
                  fontWeight: 800,
                  letterSpacing: "-0.5px",
                  color: INK,
                  "&::placeholder": { color: TEXT_FAINT },
                }}
              />
            </Stack>

            <Stack direction="row" sx={{ gap: "8px", mb: "14px" }}>
              {presets.map((preset) => (
                <Box
                  key={preset.label}
                  component="button"
                  type="button"
                  onClick={() => setAmountText(toAmountText(preset.value))}
                  sx={{
                    height: "38px",
                    p: "0 16px",
                    border: `1.5px solid ${BORDER_STRONG}`,
                    borderRadius: "999px",
                    background: "#FFFFFF",
                    color: TEXT_MUTED,
                    fontFamily: "inherit",
                    fontSize: "13.5px",
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "border-color .15s, color .15s",
                    "&:hover": { borderColor: INK, color: INK },
                  }}
                >
                  {preset.label}
                </Box>
              ))}
            </Stack>

            <Typography sx={{ fontSize: "13px", color: TEXT_FAINT, mb: "12px" }}>
              {getRemoteText(
                "bulk_pay_nearest_due_note",
                "En yakın vadeli ödemeden başlanarak kapatılır."
              )}
            </Typography>

            <Stack sx={{ maxHeight: "200px", overflowY: "auto", gap: "8px" }}>
              {allocation.covered.length === 0 && allocation.partial === null ? (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    p: "10px 12px",
                    borderRadius: "14px",
                    background: SOFT_BG,
                    fontSize: "13.5px",
                    color: TEXT_FAINT,
                    textAlign: "center",
                  }}
                >
                  {getRemoteText(
                    "bulk_pay_amount_hint",
                    "Tutar gir; en yakın vadeden başlayarak hangi ödemelerin kapanacağını gör."
                  )}
                </Stack>
              ) : (
                <>
                  {allocation.covered.map((p) => (
                    <Stack
                      key={p.id}
                      direction="row"
                      alignItems="center"
                      sx={{
                        gap: "10px",
                        p: "10px 12px",
                        borderRadius: "14px",
                        background: SOFT_BG,
                      }}
                    >
                      <Box
                        component="img"
                        src={p.shop.logoUrl}
                        alt={p.shop.name}
                        sx={{ ...rowLogoSx, width: "28px", height: "28px", borderRadius: "8px" }}
                      />
                      <Typography
                        noWrap
                        sx={{ flex: 1, minWidth: 0, fontSize: "13.5px", fontWeight: 600, color: INK }}
                      >
                        {p.shop.name}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "12.5px",
                          fontWeight: 700,
                          color: SUCCESS,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {FormatAmountSymbol(p.remainingPaymentAmount)} ·{" "}
                        {getRemoteText("bulk_pay_closes_label", "kapanır")}
                      </Typography>
                    </Stack>
                  ))}
                  {allocation.partial && (
                    <Stack
                      direction="row"
                      alignItems="center"
                      sx={{
                        gap: "10px",
                        p: "10px 12px",
                        borderRadius: "14px",
                        background: SOFT_BG,
                      }}
                    >
                      <Box
                        component="img"
                        src={allocation.partial.payment.shop.logoUrl}
                        alt={allocation.partial.payment.shop.name}
                        sx={{ ...rowLogoSx, width: "28px", height: "28px", borderRadius: "8px" }}
                      />
                      <Typography
                        noWrap
                        sx={{ flex: 1, minWidth: 0, fontSize: "13.5px", fontWeight: 600, color: INK }}
                      >
                        {allocation.partial.payment.shop.name}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "12.5px",
                          fontWeight: 700,
                          color: INK,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {FormatAmountSymbol(allocation.partial.amount)} ·{" "}
                        {getRemoteText("bulk_pay_partial_label", "kısmi")}
                      </Typography>
                    </Stack>
                  )}
                </>
              )}
            </Stack>
          </Stack>
        )}

        {/* Footer (design .bulkpay__foot) */}
        <Box sx={{ mt: "20px", pt: "18px", borderTop: `1px solid ${BORDER}` }}>
          <Stack
            direction="row"
            alignItems="baseline"
            justifyContent="space-between"
            sx={{ mb: "14px" }}
          >
            <Typography sx={{ fontSize: "14px", color: TEXT_MUTED }}>
              {getRemoteText("bulk_pay_payable_amount", "Ödenecek tutar")}
            </Typography>
            <Typography
              sx={{
                fontSize: "22px",
                fontWeight: 800,
                letterSpacing: "-0.4px",
                color: INK,
              }}
            >
              {FormatAmountSymbol(payableAmount)}
            </Typography>
          </Stack>
          <CLoadingButton
            fullWidth
            size="large"
            variant="contained"
            disabled={payDisabled}
            onClick={() =>
              onConfirm(
                mode === "amount" ? cappedAmount : undefined,
                mode === "amount"
                  ? {
                      coveredCreditIds: allocation.covered.map(
                        (p) => p.creditId
                      ),
                      partial: allocation.partial
                        ? {
                            creditId: allocation.partial.payment.creditId,
                            amount: allocation.partial.amount,
                          }
                        : undefined,
                    }
                  : undefined
              )
            }
          >
            {getRemoteText("bulk_pay_button", "Öde")}
          </CLoadingButton>
        </Box>
      </Box>
    </Dialog>
  );
};

export default BulkPayModal;
